import { useState, useEffect } from 'react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner]         = useState(false);
  const [isIOS, setIsIOS]                   = useState(false);
  const [showIOSGuide, setShowIOSGuide]     = useState(false);
  const [isInstalled, setIsInstalled]       = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    setIsIOS(ios);

    // Check if dismissed recently
    const dismissed = localStorage.getItem('vep-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    if (ios) {
      // Show iOS guide after 3 seconds
      setTimeout(() => setShowBanner(true), 3000);
    } else {
      // Listen for beforeinstallprompt (Chrome/Edge/Android)
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShowBanner(true), 3000);
      });
    }

    // Hide if installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
    });
  }, []);

  const handleInstall = async () => {
    if (isIOS) { setShowIOSGuide(true); return; }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('vep-install-dismissed', Date.now().toString());
  };

  if (isInstalled || !showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div
        style={{
          position: 'fixed', bottom: 16, left: 16, right: 16,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: 16, padding: '16px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: 99999, color: '#fff',
          display: 'flex', alignItems: 'center', gap: 14,
          border: '1px solid rgba(242,101,44,0.3)',
          animation: 'slideUp 0.3s ease',
          maxWidth: 480, margin: '0 auto',
        }}
      >
        {/* Logo */}
        <div style={{
          width: 46, height: 46, borderRadius: 12, flexShrink: 0,
          background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          <img src="/think-gas-logo.png" alt="Think Gas" style={{ width: 36, height: 36, objectFit: 'contain' }} />
        </div>

        {/* Text */}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', marginBottom: 2 }}>
            Install Think Gas VEP
          </div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)' }}>
            {isIOS ? 'Add to Home Screen for quick access' : 'Install for offline access & faster loading'}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleInstall}
            style={{
              background: '#F2652C', color: '#fff', border: 'none',
              borderRadius: 8, padding: '7px 14px', fontSize: '0.78rem',
              fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {isIOS ? 'How to' : 'Install'}
          </button>
          <button
            onClick={handleDismiss}
            style={{
              background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8, padding: '7px 10px', fontSize: '0.78rem',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* iOS Installation Guide Modal */}
      {showIOSGuide && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          zIndex: 100000, display: 'flex', alignItems: 'flex-end', padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: 28, width: '100%',
            maxWidth: 480, margin: '0 auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h5 style={{ fontWeight: 700, color: '#231F20', margin: 0 }}>Add to Home Screen</h5>
              <button onClick={() => setShowIOSGuide(false)}
                style={{ background: '#f4f4f4', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem' }}>
                ✕ Close
              </button>
            </div>

            {[
              { step: '1', icon: '⬆️', text: 'Tap the Share button at the bottom of your browser' },
              { step: '2', icon: '➕', text: 'Scroll down and tap "Add to Home Screen"' },
              { step: '3', icon: '✅', text: 'Tap "Add" to install Think Gas VEP on your device' },
            ].map(({ step, icon, text }) => (
              <div key={step} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#fdf0eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1rem', flexShrink: 0,
                }}>
                  {icon}
                </div>
                <div style={{ fontSize: '0.88rem', color: '#495057', paddingTop: 8 }}>
                  <strong>Step {step}:</strong> {text}
                </div>
              </div>
            ))}

            <button
              onClick={() => setShowIOSGuide(false)}
              style={{
                width: '100%', background: '#F2652C', color: '#fff', border: 'none',
                borderRadius: 10, padding: '12px', fontWeight: 600, cursor: 'pointer',
                fontSize: '0.9rem', marginTop: 8,
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100px); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
